// ============================================
// Modifier Collector — PJE v3.1
//
// SIMPLIFIED modifier collection:
// - Phase 1: Structured data from frontend modals
// - Phase 2: Late Reveal detection via regex/keyword
//   patterns (NOT full NLP — just safety catches)
//
// Safety-critical modifiers trigger Phase 2 re-runs.
// Preference modifiers queue for end-of-conversation.
// ============================================

import type { ModifierSet, Medication } from '../../types/patientJourney';
import { EMPTY_MODIFIER_SET } from '../../types/patientJourney';
import type { PJEConfig } from './pjeConfig';
import { getPJEConfig } from './pjeConfig';

// --- Medication Pattern Groups ---

interface PatternGroup {
  category: string;
  patterns: RegExp[];
}

const MEDICATION_PATTERNS: PatternGroup[] = [
  {
    category: 'blood_thinners',
    patterns: [
      /\bwarfarin\b/,
      /\bcoumadin\b/,
      /\beliquis\b/,
      /\bxarelto\b/,
      /\bpradaxa\b/,
      /\bheparin\b/,
      /\bblood\s+thinner\b/,
    ],
  },
  {
    category: 'ssris',
    patterns: [
      /\bprozac\b/,
      /\bzoloft\b/,
      /\blexapro\b/,
      /\bcelexa\b/,
      /\bpaxil\b/,
      /\bsertraline\b/,
      /\bfluoxetine\b/,
      /\bescitalopram\b/,
    ],
  },
  {
    category: 'statins',
    patterns: [
      /\blipitor\b/,
      /\bcrestor\b/,
      /\batorvastatin\b/,
      /\brosuvastatin\b/,
      /\bsimvastatin\b/,
      /\bcholesterol\s+medication\b/,
    ],
  },
  {
    category: 'blood_pressure',
    patterns: [
      /\blisinopril\b/,
      /\bamlodipine\b/,
      /\bmetoprolol\b/,
      /\blosartan\b/,
      /\bblood\s+pressure\s+medication\b/,
    ],
  },
  {
    category: 'diabetes',
    patterns: [
      /\bmetformin\b/,
      /\binsulin\b/,
      /\bglipizide\b/,
      /\bdiabetes\s+medication\b/,
      /\bblood\s+sugar\s+medication\b/,
    ],
  },
  {
    category: 'thyroid',
    patterns: [
      /\blevothyroxine\b/,
      /\bsynthroid\b/,
      /\barmour\s+thyroid\b/,
      /\bthyroid\s+medication\b/,
    ],
  },
  {
    category: 'birth_control',
    patterns: [
      /\bbirth\s+control\b/,
      /\bthe\s+pill\b/,
      /\boral\s+contraceptive\b/,
      /\biud\b/,
    ],
  },
  {
    category: 'immunosuppressants',
    patterns: [
      /\bprednisone\b/,
      /\bmethotrexate\b/,
      /\bhumira\b/,
      /\bimmunosuppressant\b/,
    ],
  },
];

// --- Condition Pattern Groups ---

const CONDITION_PATTERNS: PatternGroup[] = [
  {
    category: 'pregnancy_reproductive',
    patterns: [
      /\bpregnant\b/,
      /\btrying\s+to\s+conceive\b/,
      /\bbreastfeeding\b/,
      /\bnursing\b/,
    ],
  },
  {
    category: 'kidney_liver',
    patterns: [
      /\bkidney\s+disease\b/,
      /\bliver\s+disease\b/,
      /\bdialysis\b/,
      /\bhepatitis\b/,
    ],
  },
  {
    category: 'autoimmune',
    patterns: [
      /\blupus\b/,
      /\brheumatoid\s+arthritis\b/,
      /\bmultiple\s+sclerosis\b/,
      /\bcrohn'?s\b/,
    ],
  },
  {
    category: 'bleeding_disorders',
    patterns: [
      /\bhemophilia\b/,
      /\bbleeding\s+disorder\b/,
      /\bblood\s+clotting\b/,
    ],
  },
  {
    category: 'cancer',
    patterns: [
      /\bcancer\b/,
      /\bchemo\b/,
      /\bchemotherapy\b/,
      /\bradiation\b/,
    ],
  },
];

// --- Allergy Patterns ---

const ALLERGY_TRIGGER_PATTERNS: RegExp[] = [
  /\ballergic\s+to\b/,
  /\ballergy\s+to\b/,
  /\bcan'?t\s+take\b/,
  /\breact\s+to\b/,
  /\bsensitive\s+to\b/,
];

const COMMON_ALLERGENS: RegExp[] = [
  /\bshellfish\b/,
  /\bsoy\b/,
  /\bgluten\b/,
  /\bdairy\b/,
  /\beggs?\b/,
  /\bfish\b/,
  /\btree\s+nuts?\b/,
];

// --- Reproductive Status Mapping ---

const REPRODUCTIVE_STATUS_PATTERNS: {
  pattern: RegExp;
  status: ModifierSet['reproductiveStatus'];
}[] = [
  { pattern: /\bpregnant\b/, status: 'pregnant' },
  { pattern: /\btrying\s+to\s+conceive\b/, status: 'trying_to_conceive' },
  { pattern: /\bbreastfeeding\b/, status: 'breastfeeding' },
  { pattern: /\bnursing\b/, status: 'breastfeeding' },
  { pattern: /\bpostmenopausal\b/, status: 'postmenopausal' },
];

// --- Late Reveal Phrases ---

const LATE_REVEAL_PHRASES: RegExp[] = [
  /\bforgot\b/,
  /\boh\s+wait\b/,
  /\bactually\b/,
  /\bi\s+should\s+mention\b/,
  /\bi\s+didn'?t\s+tell\s+you\b/,
];

// --- Completeness Categories ---

const MODIFIER_CATEGORIES: (keyof ModifierSet)[] = [
  'allergies',
  'medications',
  'conditions',
  'lifestyle',
  'budgetTier',
  'intent',
  'reproductiveStatus',
  'priorSupplements',
  'contraindications',
  'goals',
];

// ============================================
// ModifierCollector
// ============================================

export class ModifierCollector {
  private modifiers: ModifierSet;
  private config: PJEConfig;

  constructor(config?: PJEConfig) {
    this.config = config ?? getPJEConfig();
    this.modifiers = { ...EMPTY_MODIFIER_SET };
    // Deep-copy arrays so mutations don't bleed into the constant
    this.modifiers.allergies = [];
    this.modifiers.medications = [];
    this.modifiers.conditions = [];
    this.modifiers.lifestyle = [];
    this.modifiers.priorSupplements = [];
    this.modifiers.contraindications = [];
    this.modifiers.goals = [];
  }

  // -------------------------------------------
  // Phase 1: Structured setters from modals
  // -------------------------------------------

  setGoals(goals: string[]): void {
    this.modifiers.goals = [...goals];
  }

  setMedications(medications: Medication[]): void {
    this.modifiers.medications = [...medications];
  }

  setAllergies(allergies: string[]): void {
    this.modifiers.allergies = [...allergies];
  }

  setConditions(conditions: string[]): void {
    this.modifiers.conditions = [...conditions];
  }

  setBudgetTier(tier: ModifierSet['budgetTier']): void {
    this.modifiers.budgetTier = tier;
  }

  setIntent(intent: ModifierSet['intent']): void {
    this.modifiers.intent = intent;
  }

  setReproductiveStatus(status: ModifierSet['reproductiveStatus']): void {
    this.modifiers.reproductiveStatus = status;
  }

  setPriorSupplements(supplements: string[]): void {
    this.modifiers.priorSupplements = [...supplements];
  }

  // -------------------------------------------
  // Phase 2: Late Reveal detection
  // -------------------------------------------

  /**
   * Scan a free-text message for medication, condition, and allergy
   * mentions that were NOT collected during Phase 1 modals.
   *
   * Returns null if nothing new detected.
   * Returns a Partial<ModifierSet> with only newly detected fields.
   */
  detectLateReveal(message: string): Partial<ModifierSet> | null {
    const lower = message.toLowerCase();
    const isLateRevealPhrase = LATE_REVEAL_PHRASES.some((p) => p.test(lower));

    const detected: Partial<ModifierSet> = {};
    let hasNewModifier = false;

    // --- Medication detection ---
    const detectedMedications: Medication[] = [];
    for (const group of MEDICATION_PATTERNS) {
      for (const pattern of group.patterns) {
        if (pattern.test(lower)) {
          // Extract the matched drug name
          const match = lower.match(pattern);
          const drugName = match ? match[0] : group.category;

          // Skip if already known
          const alreadyKnown = this.modifiers.medications.some(
            (m) => m.name.toLowerCase() === drugName || m.category === group.category
          );
          if (!alreadyKnown) {
            detectedMedications.push({
              name: drugName,
              dose: 'unknown',
              category: group.category,
              duration: 'unknown',
            });
          }
        }
      }
    }
    if (detectedMedications.length > 0) {
      detected.medications = detectedMedications;
      hasNewModifier = true;
    }

    // --- Condition detection ---
    const detectedConditions: string[] = [];
    for (const group of CONDITION_PATTERNS) {
      for (const pattern of group.patterns) {
        if (pattern.test(lower)) {
          const match = lower.match(pattern);
          const conditionName = match ? match[0] : group.category;

          // Skip if already known
          const alreadyKnown = this.modifiers.conditions.some(
            (c) => c.toLowerCase() === conditionName
          );
          if (!alreadyKnown) {
            detectedConditions.push(conditionName);
          }
        }
      }
    }
    if (detectedConditions.length > 0) {
      detected.conditions = detectedConditions;
      hasNewModifier = true;
    }

    // --- Reproductive status detection ---
    for (const { pattern, status } of REPRODUCTIVE_STATUS_PATTERNS) {
      if (pattern.test(lower) && this.modifiers.reproductiveStatus !== status) {
        detected.reproductiveStatus = status;
        hasNewModifier = true;
        break; // first match wins
      }
    }

    // --- Allergy detection ---
    const hasAllergyTrigger = ALLERGY_TRIGGER_PATTERNS.some((p) => p.test(lower));
    if (hasAllergyTrigger) {
      const detectedAllergies: string[] = [];
      for (const allergenPattern of COMMON_ALLERGENS) {
        if (allergenPattern.test(lower)) {
          const match = lower.match(allergenPattern);
          const allergenName = match ? match[0] : '';
          if (allergenName && !this.modifiers.allergies.some(
            (a) => a.toLowerCase() === allergenName
          )) {
            detectedAllergies.push(allergenName);
          }
        }
      }
      if (detectedAllergies.length > 0) {
        detected.allergies = detectedAllergies;
        hasNewModifier = true;
      }
    }

    // If a late reveal phrase is present, lower the detection threshold:
    // treat allergy-adjacent allergen mentions as matches even without
    // an explicit trigger phrase like "allergic to".
    if (isLateRevealPhrase && !detected.allergies) {
      const detectedAllergies: string[] = [];
      for (const allergenPattern of COMMON_ALLERGENS) {
        if (allergenPattern.test(lower)) {
          const match = lower.match(allergenPattern);
          const allergenName = match ? match[0] : '';
          if (allergenName && !this.modifiers.allergies.some(
            (a) => a.toLowerCase() === allergenName
          )) {
            detectedAllergies.push(allergenName);
          }
        }
      }
      if (detectedAllergies.length > 0) {
        detected.allergies = detectedAllergies;
        hasNewModifier = true;
      }
    }

    if (!hasNewModifier) {
      return null;
    }

    // Merge detected modifiers into internal state
    if (detected.medications) {
      this.modifiers.medications = [
        ...this.modifiers.medications,
        ...detected.medications,
      ];
    }
    if (detected.conditions) {
      this.modifiers.conditions = [
        ...this.modifiers.conditions,
        ...detected.conditions,
      ];
    }
    if (detected.reproductiveStatus) {
      this.modifiers.reproductiveStatus = detected.reproductiveStatus;
    }
    if (detected.allergies) {
      this.modifiers.allergies = [
        ...this.modifiers.allergies,
        ...detected.allergies,
      ];
    }

    return detected;
  }

  // -------------------------------------------
  // Categorize and decide
  // -------------------------------------------

  /**
   * Classify a modifier type as safety-critical or preference
   * based on config.modifierCategories.
   */
  categorizeModifier(modifierType: string): 'safety_critical' | 'preference' {
    if (this.config.modifierCategories.safetyCritical.includes(modifierType)) {
      return 'safety_critical';
    }
    // Default to preference for unknown types — safe because
    // preference modifiers don't trigger re-runs.
    return 'preference';
  }

  /**
   * Returns true if any newly detected modifier is safety-critical,
   * meaning Phase 2 protocol generation must re-run.
   */
  shouldRerunPhase2(newModifiers: Partial<ModifierSet>): boolean {
    const safetyCritical = this.config.modifierCategories.safetyCritical;

    for (const key of Object.keys(newModifiers) as (keyof ModifierSet)[]) {
      if (safetyCritical.includes(key)) {
        return true;
      }
    }

    return false;
  }

  // -------------------------------------------
  // State accessors
  // -------------------------------------------

  /** Return a deep copy of the current modifier set. */
  getModifiers(): ModifierSet {
    return {
      allergies: [...this.modifiers.allergies],
      medications: [...this.modifiers.medications],
      conditions: [...this.modifiers.conditions],
      lifestyle: [...this.modifiers.lifestyle],
      budgetTier: this.modifiers.budgetTier,
      intent: this.modifiers.intent,
      reproductiveStatus: this.modifiers.reproductiveStatus,
      priorSupplements: [...this.modifiers.priorSupplements],
      contraindications: [...this.modifiers.contraindications],
      goals: [...this.modifiers.goals],
    };
  }

  /**
   * Percentage of modifier categories that have data (0-100).
   *
   * Array fields count as "has data" if length > 0.
   * Enum fields count as "has data" if not at default value.
   */
  getCompleteness(): number {
    let filled = 0;

    for (const category of MODIFIER_CATEGORIES) {
      if (this.categoryHasData(category)) {
        filled++;
      }
    }

    return Math.round((filled / MODIFIER_CATEGORIES.length) * 100);
  }

  /** Return names of categories that have no data yet. */
  getMissedCategories(): string[] {
    const missed: string[] = [];

    for (const category of MODIFIER_CATEGORIES) {
      if (!this.categoryHasData(category)) {
        missed.push(category);
      }
    }

    return missed;
  }

  // -------------------------------------------
  // Internal helpers
  // -------------------------------------------

  private categoryHasData(category: keyof ModifierSet): boolean {
    const value = this.modifiers[category];

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // Enum fields: check against EMPTY_MODIFIER_SET defaults
    const defaultValue = EMPTY_MODIFIER_SET[category];
    return value !== defaultValue;
  }
}
